import {
    AssetVideo,
    CourseAccreditation,
    CourseAccreditationType,
    CourseBadge,
    CourseBadgeType,
    CourseCaption,
    CaptionType,
    CourseCategory,
    CourseImages,
    CourseSubcategory,
    DifficultyLevel,
    Topic,
    ArticleLecture,
    AudioLecture,
    CodingExercise,
    Curriculum,
    CourseSection,
    EbookLecture,
    ExternalLecture,
    FileLecture,
    PracticeAssignment,
    PracticeTest,
    PresentationLecture,
    Quiz,
    RolePlay,
    VideoLecture,
    VideoMashupLecture,
    CurriculumItemType,
    Instructor,
    ContentCounts,
    adaptCourseBadges,
    sortCourseBadgesByPriority,
} from '@udemy-v2/browse-course';
import {
    ClpCourseQuery,
    Course as GraphQLCourse,
    CourseInstructor as GraphQLCourseInstructor,
    Topic as GraphQLTopic,
    DifficultyLevel as GraphQLDifficultyLevel,
    Curriculum as GraphQLCurriculum,
    CurriculumItem as GraphQLCurriculumItem,
    CourseSection as GraphQLCourseSection,
    VideoLecture as GraphQLVideoLecture,
    VideoMashupLecture as GraphQLVideoMashupLecture,
    ArticleLecture as GraphQLArticleLecture,
    PresentationLecture as GraphQLPresentationLecture,
    EBookLecture as GraphQLEBookLecture,
    AudioLecture as GraphQLAudioLecture,
    FileLecture as GraphQLFileLecture,
    ExternalLecture as GraphQLExternalLecture,
    Quiz as GraphQLQuiz,
    PracticeTest as GraphQLPracticeTest,
    PracticeAssignment as GraphQLPracticeAssignment,
    CodingExercise as GraphQLCodingExercise,
    RolePlay as GraphQLRolePlay,
    CourseAccreditationType as GraphQLCourseAccreditationType,
    CaptionType as GraphQLCaptionType,
    AssetVideo as GraphQLAssetVideo,
} from '@/generated/graphql';
import {ClpCourse} from '@/types/course';

/**
 * Type definition for the result of the ClpCourseQuery
 * Represents the course data returned by the GraphQL query rather than the full Course type defined
 * in the GraphQL schema.
 */
export type GraphQLCourseQueryResult = NonNullable<ClpCourseQuery['courseByLandingURL']>;

const NUMBER_OF_BADGES_TO_SHOW = 2;

/**
 * Map difficulty level from GraphQL to application type
 */
export const mapDifficultyLevel = (
    level: GraphQLDifficultyLevel | null | undefined,
): DifficultyLevel | undefined => {
    if (!level) return undefined;

    switch (level) {
        case GraphQLDifficultyLevel.AllLevels:
            return DifficultyLevel.ALL_LEVELS;
        case GraphQLDifficultyLevel.Beginner:
            return DifficultyLevel.BEGINNER;
        case GraphQLDifficultyLevel.Intermediate:
            return DifficultyLevel.INTERMEDIATE;
        case GraphQLDifficultyLevel.Expert:
            return DifficultyLevel.EXPERT;
        default:
            return undefined;
    }
};

/**
 * Map GraphQL CourseAccreditationType to application's CourseAccreditationType
 */
const mapCourseAccreditationType = (
    type: GraphQLCourseAccreditationType,
): CourseAccreditationType => {
    switch (type) {
        case GraphQLCourseAccreditationType.Cpe:
            return CourseAccreditationType.CPE;
        default:
            return CourseAccreditationType.CPE; // Default fallback
    }
};

/**
 * Adapts a GraphQL AssetVideo to the application's AssetVideo type
 * @param asset - GraphQL AssetVideo object
 * @returns AssetVideo object for our application
 */
export const adaptAsset = (asset: GraphQLAssetVideo): AssetVideo => {
    return {
        id: asset.id,
        duration: asset.duration,
        accessToken: asset.accessToken || undefined,
        isDrmProtected: asset.isDrmProtected || false,
        mediaSources: (asset.mediaSources || []).map((source) => ({
            label: source.label,
            src: source.src,
            mediaType: source.mediaType,
        })),
        downloads: (asset.downloads || []).map((download) => ({
            label: download.label,
            urlFile: download.urlFile,
        })),
        captions: (asset.captions || []).map((caption) => ({
            id: caption.id,
            fileName: '', // GraphQL doesn't provide fileName, so we use empty string
            language: caption.language,
            source: caption.source === 'AUTO' ? 'AUTO' : 'MANUAL',
            status: caption.status,
            title: caption.videoLabel,
            url: caption.url,
            videoLabel: caption.videoLabel,
        })),
        thumbnailTrack: asset.thumbnailTrack
            ? {
                  imgUrl: asset.thumbnailTrack.imgUrl,
                  vttUrl: asset.thumbnailTrack.vttUrl,
              }
            : undefined,
    };
};

/**
 * Maps GraphQL CaptionType to application's CaptionType
 */
const mapCaptionType = (type: GraphQLCaptionType): CaptionType => {
    switch (type) {
        case GraphQLCaptionType.Subtitle:
            return CaptionType.Subtitle;
        default:
            return CaptionType.Subtitle;
    }
};

/**
 * Adapts GraphQL CourseImages to our application's CourseImages type
 * @param images - GraphQL CourseImages object
 * @returns CourseImages object for our application
 */
const adaptCourseImages = (images: GraphQLCourse['images']): CourseImages => {
    return {
        px48x27: images.px48x27 || undefined,
        px50x50: images.px50x50 || undefined,
        px75x75: images.px75x75 || undefined,
        px96x54: images.px96x54 || undefined,
        px100x100: images.px100x100 || undefined,
        px240x135: images.px240x135 || undefined,
        px304x171: images.px304x171 || undefined,
        px480x270: images.px480x270 || undefined,
        height125: images.height125 || undefined,
        height200: images.height200 || undefined,
    };
};

/**
 * Adapts GraphQL CourseInstructor to our application's instructor type
 * @param instructor - GraphQL CourseInstructor object
 * @returns instructor object for our application
 */
const adaptCourseInstructor = (instructor: GraphQLCourseInstructor): Instructor => {
    return {
        id: instructor.id,
        name: instructor.name ?? '',
        images: {
            small: instructor.images?.px50x50 ?? undefined,
        },
        // HACK HACK HACK: Just use the pathname of the URL with a trailing slash so it aligns with the monolith API format
        url: instructor.url
            ? instructor.url.startsWith('http')
                ? new URL(instructor.url).pathname + '/'
                : instructor.url.endsWith('/')
                  ? instructor.url
                  : instructor.url + '/'
            : undefined,
    };
};

/**
 * Adapts GraphQL CourseCategory to our application's CourseCategory type
 * @param category - GraphQL CourseCategory object
 * @returns CourseCategory object for our application
 */
const adaptCourseCategory = (
    category: Pick<GraphQLCourse['categories'][0], 'id' | 'name' | 'url' | 'subcategories'>,
): CourseCategory => {
    return {
        id: category.id,
        name: category.name,
        url: category.url,
        subcategories: category.subcategories.map((courseSubcategory) => {
            const subcategory = courseSubcategory as CourseSubcategory;
            return {
                id: subcategory.id,
                name: subcategory.name,
                url: subcategory.url,
            };
        }),
    };
};

/**
 * Adapts GraphQL Topic to our application's Topic type
 * @param topic - GraphQL Topic object
 * @returns Topic object for our application
 */
const adaptTopic = (topic: Pick<GraphQLTopic, 'id' | 'name' | 'url'>): Topic => {
    return {
        id: topic.id,
        name: topic.name,
        url: topic.url,
    };
};

/**
 * Transform a curriculum item from GraphQL to the application type
 */
const transformCurriculumItem = (
    item: GraphQLCurriculumItem,
):
    | VideoLecture
    | VideoMashupLecture
    | ArticleLecture
    | PresentationLecture
    | EbookLecture
    | AudioLecture
    | FileLecture
    | ExternalLecture
    | Quiz
    | PracticeTest
    | PracticeAssignment
    | CodingExercise
    | RolePlay
    | undefined => {
    switch ((item as {__typename?: string}).__typename) {
        case 'VideoLecture': {
            const videoLecture = item as GraphQLVideoLecture;
            const videoThumbnail = videoLecture.images?.height100 ?? undefined;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.VIDEO_LECTURE,
                description: videoLecture.description?.toString() || undefined,
                durationInSeconds: videoLecture.durationInSeconds || 0,
                thumbnail: videoThumbnail,
                urlAutoEnroll: videoLecture.urlAutoEnroll?.toString() || '',
                urlLanding: videoLecture.urlLanding?.toString() || '',
                images: {
                    thumbnail: videoThumbnail,
                },
                asset: videoLecture.asset?.id ? ({id: videoLecture.asset.id} as AssetVideo) : undefined,
                isPreviewable: videoLecture.isPreviewable || false,
                aiSummary: videoLecture.aiSummary || undefined,
            } as VideoLecture;
        }

        case 'VideoMashupLecture': {
            const mashupLecture = item as GraphQLVideoMashupLecture;
            const mashupThumbnail = mashupLecture.images?.height100 ?? undefined;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.VIDEO_MASHUP_LECTURE,
                description: mashupLecture.description?.toString() || undefined,
                durationInSeconds: mashupLecture.durationInSeconds || 0,
                thumbnail: mashupThumbnail,
                urlAutoEnroll: mashupLecture.urlAutoEnroll?.toString() || '',
                urlLanding: mashupLecture.urlLanding?.toString() || '',
                images: {
                    thumbnail: mashupThumbnail,
                },
                asset: mashupLecture.asset?.id ? ({id: mashupLecture.asset.id} as AssetVideo) : undefined,
                isPreviewable: mashupLecture.isPreviewable || false,
            } as VideoMashupLecture;
        }

        case 'ArticleLecture': {
            const articleLecture = item as GraphQLArticleLecture;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.ARTICLE_LECTURE,
                description: articleLecture.description?.toString() || undefined,
                durationInSeconds: articleLecture.durationInSeconds || 0,
                thumbnail: articleLecture.thumbnail?.toString(),
                urlAutoEnroll: articleLecture.urlAutoEnroll?.toString() || '',
                urlLanding: articleLecture.urlLanding?.toString() || '',
                images: {
                    thumbnail: articleLecture.thumbnail?.toString(),
                },
                isPreviewable: articleLecture.isPreviewable || false,
            };
        }

        case 'PresentationLecture': {
            const presentationLecture = item as GraphQLPresentationLecture;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.PRESENTATION_LECTURE,
                description: presentationLecture.description?.toString() || undefined,
                durationInSeconds: presentationLecture.durationInSeconds || 0,
                thumbnail: presentationLecture.thumbnail?.toString(),
                urlAutoEnroll: presentationLecture.urlAutoEnroll?.toString() || '',
                urlLanding: presentationLecture.urlLanding?.toString() || '',
                images: {
                    thumbnail: presentationLecture.thumbnail?.toString(),
                },
                isPreviewable: presentationLecture.isPreviewable || false,
            };
        }

        case 'EBookLecture': {
            const ebookLecture = item as GraphQLEBookLecture;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.EBOOK_LECTURE,
                description: ebookLecture.description?.toString() || undefined,
                durationInSeconds: ebookLecture.durationInSeconds || 0,
                thumbnail: ebookLecture.thumbnail?.toString(),
                urlAutoEnroll: ebookLecture.urlAutoEnroll?.toString() || '',
                urlLanding: ebookLecture.urlLanding?.toString() || '',
                images: {
                    thumbnail: ebookLecture.thumbnail?.toString(),
                },
                isPreviewable: ebookLecture.isPreviewable || false,
            };
        }

        case 'AudioLecture': {
            const audioLecture = item as GraphQLAudioLecture;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.AUDIO_LECTURE,
                description: audioLecture.description?.toString() || undefined,
                durationInSeconds: audioLecture.durationInSeconds || 0,
                thumbnail: audioLecture.thumbnail?.toString(),
                urlAutoEnroll: audioLecture.urlAutoEnroll?.toString() || '',
                urlLanding: audioLecture.urlLanding?.toString() || '',
                images: {
                    thumbnail: audioLecture.thumbnail?.toString(),
                },
                isPreviewable: audioLecture.isPreviewable || false,
            };
        }

        case 'FileLecture': {
            const fileLecture = item as GraphQLFileLecture;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.FILE_LECTURE,
                description: fileLecture.description?.toString() || undefined,
                durationInSeconds: 0, // File lectures typically don't have duration
                thumbnail: fileLecture.thumbnail?.toString(),
                urlAutoEnroll: fileLecture.urlAutoEnroll?.toString() || '',
                urlLanding: fileLecture.urlLanding?.toString() || '',
                images: {
                    thumbnail: fileLecture.thumbnail?.toString(),
                },
                isPreviewable: fileLecture.isPreviewable || false,
            };
        }

        case 'ExternalLecture': {
            const externalLecture = item as GraphQLExternalLecture;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.EXTERNAL_LECTURE,
                description: externalLecture.description?.toString() || undefined,
                durationInSeconds: 0, // External lectures typically don't have duration
                thumbnail: externalLecture.thumbnail?.toString(),
                urlAutoEnroll: externalLecture.urlAutoEnroll?.toString() || '',
                urlLanding: externalLecture.urlLanding?.toString() || '',
                images: {
                    thumbnail: externalLecture.thumbnail?.toString(),
                },
                isPreviewable: externalLecture.isPreviewable || false,
            };
        }

        case 'Quiz': {
            const quiz = item as GraphQLQuiz;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.QUIZ,
                description: quiz.description?.toString(),
                urlLanding: quiz.urlLanding?.toString() || '',
            };
        }

        case 'PracticeTest': {
            const practiceTest = item as GraphQLPracticeTest;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.PRACTICE_TEST,
                minimumPassingScore: practiceTest.minimumPassingScore || 0,
                randomized: practiceTest.randomized || false,
                urlLanding: practiceTest.urlLanding?.toString() || '',
            };
        }

        case 'PracticeAssignment': {
            const practiceAssignment = item as GraphQLPracticeAssignment;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.PRACTICE_ASSIGNMENT,
                description: practiceAssignment.description?.toString(),
                urlLanding: practiceAssignment.urlLanding?.toString() || '',
            };
        }

        case 'CodingExercise': {
            const codingExercise = item as GraphQLCodingExercise;
            return {
                id: item.id,
                title: item.title || '',
                type: CurriculumItemType.CODING_EXERCISE,
                description: codingExercise.description?.toString(),
                urlLanding: codingExercise.urlLanding?.toString() || '',
            };
        }

        case 'RolePlay': {
            const rolePlay = item as GraphQLRolePlay;
            return {
                id: rolePlay.id,
                title: rolePlay.title,
                type: CurriculumItemType.ROLE_PLAY,
            };
        }

        default:
            return undefined;
    }
};

/**
 * Transform a curriculum section from GraphQL to the application type
 */
const adaptCurriculumSection = (section: GraphQLCourseSection): CourseSection => {
    const items = section.items
        .map((item) => transformCurriculumItem(item))
        .filter((item): item is NonNullable<typeof item> => item !== undefined);

    // Calculate lecture count (exclude quizzes, practice tests, assignments, and coding exercises)
    const lectureCount = items.filter((item) => {
        return ![
            CurriculumItemType.QUIZ,
            CurriculumItemType.PRACTICE_TEST,
            CurriculumItemType.PRACTICE_ASSIGNMENT,
            CurriculumItemType.CODING_EXERCISE,
        ].includes(item.type);
    }).length;

    // Calculate content length by summing durationInSeconds for items that have it
    const contentLength = items.reduce((total, item) => {
        // Check if the item has durationInSeconds property (lectures do, but quizzes/tests don't)
        if ('durationInSeconds' in item && typeof item.durationInSeconds === 'number') {
            return total + item.durationInSeconds;
        }
        return total;
    }, 0);

    return {
        id: section.id,
        title: section.title || '',
        type: CurriculumItemType.COURSE_SECTION,
        description: section.description?.toString(),
        items,
        lectureCount,
        contentLength,
    };
};

/**
 * Adapt a curriculum from GraphQL to the application type
 */
const adaptCurriculum = (
    curriculum: Pick<GraphQLCurriculum, 'sections' | 'contentCounts'>,
): Curriculum => {
    const contentCounts: Partial<ContentCounts> = {};

    // Only include non-null/undefined values to avoid nullish properties in the result
    if (curriculum.contentCounts?.lecturesCount != null) {
        contentCounts.lecturesCount = curriculum.contentCounts.lecturesCount;
    }
    if (curriculum.contentCounts?.quizzesCount != null) {
        contentCounts.quizzesCount = curriculum.contentCounts.quizzesCount;
    }
    if (curriculum.contentCounts?.codingExercisesCount != null) {
        contentCounts.codingExercisesCount = curriculum.contentCounts.codingExercisesCount;
    }
    if (curriculum.contentCounts?.practiceAssignmentsCount != null) {
        contentCounts.practiceAssignmentsCount = curriculum.contentCounts.practiceAssignmentsCount;
    }
    if (curriculum.contentCounts?.practiceTestsCount != null) {
        contentCounts.practiceTestsCount = curriculum.contentCounts.practiceTestsCount;
    }
    if (curriculum.contentCounts?.practiceTestQuestionsCount != null) {
        contentCounts.practiceTestQuestionsCount =
            curriculum.contentCounts.practiceTestQuestionsCount;
    }
    if (curriculum.contentCounts?.rolePlaysCount != null) {
        contentCounts.rolePlaysCount = curriculum.contentCounts.rolePlaysCount;
    }
    if (curriculum.contentCounts?.downloadableResourcesCount != null) {
        contentCounts.additionalResourcesCount =
            curriculum.contentCounts.downloadableResourcesCount;
    }
    if (curriculum.contentCounts?.inCourseLabsCount != null) {
        contentCounts.inCourseLabsCount = curriculum.contentCounts.inCourseLabsCount;
    }

    return {
        sections: curriculum.sections.map((section) => adaptCurriculumSection(section)),
        contentCounts: contentCounts as ContentCounts,
    };
};

/**
 * Counts the number of quizzes in a curriculum
 */
export function getNumQuizzes(curriculum?: Curriculum): number {
    if (!curriculum) {
        return 0;
    }

    return curriculum.sections.reduce((total, section) => {
        return total + section.items.filter((item) => item.type === CurriculumItemType.QUIZ).length;
    }, 0);
}

/**
 * Counts the number of coding exercises in a curriculum
 */
export function getNumCodingExercises(curriculum?: Curriculum): number {
    if (!curriculum) {
        return 0;
    }

    return curriculum.sections.reduce((total, section) => {
        return (
            total +
            section.items.filter((item) => item.type === CurriculumItemType.CODING_EXERCISE).length
        );
    }, 0);
}

/**
 * Adapts a GraphQL Course object into our application's Course type
 * @param graphqlCourse - GraphQL Course object from the API
 * @returns Course object for our application (potentially with variation aggregation)
 */
export const adaptCourse = (graphqlCourse: GraphQLCourseQueryResult): ClpCourse => {
    // Adapt primary topic if it exists
    const primaryTopic = graphqlCourse.primaryTopic
        ? {
              ...adaptTopic(graphqlCourse.primaryTopic),
          }
        : undefined;

    // Create base course object with required fields
    const result: Partial<ClpCourse> = {
        id: graphqlCourse.id,
        title: graphqlCourse.title,
        learningOutcomes: graphqlCourse.learningOutcomes,
        prerequisites: graphqlCourse.prerequisites,
        targetAudience: graphqlCourse.targetAudience,
        enrollable: graphqlCourse.enrollable,
        isFree: graphqlCourse.isFree,
        isPublished: graphqlCourse.isPublished,
        locale: graphqlCourse.locale.toString(),
        hasCertificate: graphqlCourse.hasCertificate,
        urlMobileNativeDeeplink: graphqlCourse.urlMobileNativeDeeplink.toString(),
        images: adaptCourseImages(graphqlCourse.images),
        instructors: graphqlCourse.instructors.map(adaptCourseInstructor),
        owner: graphqlCourse.owner
            ? {
                  id: graphqlCourse.owner.id,
                  name: graphqlCourse.owner.name ?? '',
                  isOptedIntoDeals: graphqlCourse.owner.isOptedIntoDeals ?? undefined,
              }
            : undefined,
        categories: graphqlCourse.categories.map((category) => adaptCourseCategory(category)),
        rating: {
            average: graphqlCourse.rating?.average
                ? Number(graphqlCourse.rating.average)
                : undefined,
            count: graphqlCourse.rating?.count ?? 0,
        },
        isPracticeTestCourse: graphqlCourse.isPracticeTestCourse,
    };

    // Add optional fields only if they exist
    if (graphqlCourse.headline) {
        result.headline = graphqlCourse.headline;
    }

    if (graphqlCourse.description) {
        result.description = graphqlCourse.description.toString();
    }

    if (graphqlCourse.duration) {
        result.duration = graphqlCourse.duration.toString();
    }

    if (graphqlCourse.durationInSeconds) {
        result.durationInSeconds = graphqlCourse.durationInSeconds;
    }

    if (graphqlCourse.durationVideoContent) {
        result.durationVideoContent = graphqlCourse.durationVideoContent;
    }

    if (graphqlCourse.level) {
        result.level = mapDifficultyLevel(graphqlCourse.level);
    }

    if (graphqlCourse.updatedOn) {
        result.updatedOn = graphqlCourse.updatedOn.toString();
    }

    if (graphqlCourse.urlAutoEnroll) {
        result.urlAutoEnroll = graphqlCourse.urlAutoEnroll.toString();
    }

    if (graphqlCourse.urlCourseLanding) {
        result.urlCourseLanding = graphqlCourse.urlCourseLanding.toString();
    }

    if (graphqlCourse.urlCourseTaking) {
        result.urlCourseTaking = graphqlCourse.urlCourseTaking.toString();
    }

    if (graphqlCourse.topics) {
        result.topics = graphqlCourse.topics.map((topic) => adaptTopic(topic));
    }

    if (primaryTopic) {
        result.primaryTopic = primaryTopic;
    }

    if (graphqlCourse.promoVideo?.asset?.__typename === 'AssetVideo') {
        result.promoVideoAsset = graphqlCourse.promoVideo.asset as GraphQLAssetVideo;
    }

    if (graphqlCourse.contentCollectionInfo) {
        result.contentCollectionInfo = {
            isInGooglePartnerCollection:
                graphqlCourse.contentCollectionInfo.isInGooglePartnerCollection,
        };
    }

    // Transform captions if they exist
    if (graphqlCourse.captions && graphqlCourse.captions.length > 0) {
        result.captions = graphqlCourse.captions.map((caption): CourseCaption => {
            return {
                locale: caption.locale.toString(),
                isAutoGenerated: caption.automaticallyGenerated,
                type: mapCaptionType(caption.type),
            };
        });
    }

    // Transform accreditations if they exist
    if (graphqlCourse.accreditations) {
        result.accreditations = graphqlCourse.accreditations.map(
            (accreditation): CourseAccreditation => {
                return {
                    compliantSince: accreditation.compliantSince?.toString(),
                    creditCount: Number(accreditation.creditCount),
                    level: accreditation.level || undefined,
                    subject: accreditation.subject || undefined,
                    type: mapCourseAccreditationType(accreditation.type),
                };
            },
        );
    }

    // Only add enrollments if there are actual enrollments to display
    if (graphqlCourse.enrollments && graphqlCourse.enrollments.roundedCount > 0) {
        result.enrollments = {
            count: graphqlCourse.enrollments.count || graphqlCourse.enrollments.roundedCount,
            roundedCount: graphqlCourse.enrollments.roundedCount,
        };
    }

    // Add curriculum if it exists
    if (graphqlCourse.curriculum) {
        const adaptedCurriculum = adaptCurriculum(
            graphqlCourse.curriculum as unknown as Pick<
                GraphQLCurriculum,
                'sections' | 'contentCounts'
            >,
        );
        result.curriculum = adaptedCurriculum;

        // Calculate total audio content duration
        result.durationAudioContent = adaptedCurriculum.sections.reduce((sectionTotal, section) => {
            return (
                sectionTotal +
                section.items.reduce((itemTotal, item) => {
                    return item.type === CurriculumItemType.AUDIO_LECTURE
                        ? itemTotal + (item.durationInSeconds || 0)
                        : itemTotal;
                }, 0)
            );
        }, 0);
    }

    // Process badges: adapt from GraphQL, add computed badges, sort, and optionally limit
    // Note: This must be done AFTER curriculum is set so we can check rolePlaysCount
    if (graphqlCourse.badges) {
        // First, adapt and deduplicate GraphQL badges from the API
        const adaptedBadges: CourseBadge[] = adaptCourseBadges(
            graphqlCourse.badges
                .filter((badge) => badge?.name)
                .map((badge) => ({
                    name: badge.name as string,
                    __typename: badge.__typename,
                })),
            undefined, // Use default priority
            true, // Deduplicate API badges
            undefined, // Don't limit yet - we'll apply limit after adding computed badges and sorting
        );

        // Add client-side computed badges (only if not already present from API)
        // Note: The Premium badge logic is intentionally not included here because it must be computed
        //  client-side. Therefore the ClpCourseProvider is responsible for injecting it.
        const computedBadges: CourseBadge[] = [];

        // Role Play badge (for courses with role play content)
        const hasRolePlayBadge = adaptedBadges.some(
            (badge) =>
                badge.type === CourseBadgeType.ROLE_PLAY ||
                badge.name === CourseBadgeType.ROLE_PLAY,
        );
        const rolePlaysCount = result.curriculum?.contentCounts?.rolePlaysCount ?? 0;
        if (rolePlaysCount > 0 && !hasRolePlayBadge) {
            computedBadges.push({
                name: CourseBadgeType.ROLE_PLAY,
                type: CourseBadgeType.ROLE_PLAY,
            });
        }

        // Combine API badges (already deduplicated) with computed badges, then sort
        const combinedBadges = [...adaptedBadges, ...computedBadges];
        result.badges = sortCourseBadgesByPriority(combinedBadges).slice(
            0,
            NUMBER_OF_BADGES_TO_SHOW,
        );
    }

    if (graphqlCourse.contentCollectionInfo) {
        result.isInGooglePartnerCollection = Boolean(
            graphqlCourse.contentCollectionInfo?.isInGooglePartnerCollection,
        );
    }

    return result as ClpCourse;
};
